import { Link } from "react-router-dom";
import { ShoppingCart, Eye } from "lucide-react";
import { useCart, useAuth } from "../App";
import { Button } from "./ui/button";
import { motion } from "framer-motion";

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart } = useCart();
  const { user } = useAuth();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart(product.product_id, 1);
  };

  const formatWeight = (weight) => {
    const weightStr = String(weight).toLowerCase().trim();

    if (weightStr === "1000" || weightStr === "1000g" || weightStr === "1kg") {
      return "1kg";
    }

    if (weightStr === "250" || weightStr === "250g" || weightStr === "250gm") {
      return "250g";
    }

    if (weightStr === "500" || weightStr === "500g" || weightStr === "500gm") {
      return "500g";
    }

    const weightNum = Number(weight);
    if (!Number.isNaN(weightNum)) {
      if (weightNum >= 1000) return `${weightNum / 1000}kg`;
      return `${weightNum}g`;
    }

    return weight;
  };

  const fallbackImage = "/images/story-chilli.jpg";

  const getProductImage = () => {
    const weightStr = String(product.weight).toLowerCase().trim();

    if (
      weightStr === "250" ||
      weightStr === "250g" ||
      weightStr === "250gm"
    ) {
      return "/images/btchilli-250gm.png";
    }

    if (
      weightStr === "500" ||
      weightStr === "500g" ||
      weightStr === "500gm"
    ) {
      return "/images/btchilli-500gm.png";
    }

    if (
      weightStr === "1000" ||
      weightStr === "1000g" ||
      weightStr === "1kg"
    ) {
      return "/images/btchilli-1kg.png";
    }

    return fallbackImage;
  };

  const imageSrc = getProductImage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Link
        to={`/products/${product.product_id}`}
        className="block bg-white border border-gray-200 rounded-[28px] overflow-hidden group hover:-translate-y-1 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
        data-testid={`product-card-${product.product_id}`}
      >
        {/* Image */}
        <div className="relative aspect-square bg-white flex items-center justify-center">
          <img
            src={imageSrc}
            alt={product.name}
            className="w-full h-full object-contain p-4"
            onError={(e) => {
              e.currentTarget.src = fallbackImage;
            }}
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            <span className="bg-[#b30f1d] text-white text-sm px-4 py-1.5 rounded-full font-extrabold shadow-md">
              {formatWeight(product.weight)}
            </span>

            {product.stock <= product.low_stock_threshold && product.stock > 0 && (
              <span className="bg-yellow-100 text-yellow-800 border border-yellow-300 text-xs px-3 py-1 rounded-full font-semibold">
                Low Stock
              </span>
            )}

            {product.stock === 0 && (
              <span className="bg-red-100 text-red-700 border border-red-300 text-xs px-3 py-1 rounded-full font-semibold">
                Out of Stock
              </span>
            )}
          </div>

          {/* Quick View Overlay */}
          <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <span className="flex items-center gap-2 text-gray-900 text-sm font-semibold bg-white/90 px-4 py-2 rounded-full shadow-md">
              <Eye className="w-4 h-4" />
              View Details
            </span>
          </div>
        </div>

        {/* Content */}
<div className="p-6 space-y-4 bg-white">

  {/* Product Name */}
  <h3 className="text-lg md:text-xl font-semibold text-gray-900 leading-snug tracking-wide">
    {product.name}
  </h3>

  {/* Price + Button */}
  <div className="flex items-center justify-between pt-1">

    {/* Price */}
    <span className="text-2xl font-semibold text-gray-900 tracking-tight">
      ₹{product.price}
    </span>

    {/* Add Button */}
    {user && product.stock > 0 && (
      <Button
        size="sm"
        onClick={handleAddToCart}
        className="bg-[#b30f1d] hover:bg-[#8f0c17] text-white rounded-full px-5 py-2 text-sm font-medium shadow-md"
        data-testid={`add-to-cart-${product.product_id}`}
      >
        <ShoppingCart className="w-4 h-4 mr-2" />
        Add
      </Button>
    )}

  </div>

</div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;